/**
 * Notifications, once on (docs/mockups/notifications.html, slide 3): every kind with its
 * switch, the morning reminder's and the streak saver's times, quiet hours (nest kinds
 * only), and "Send a test notification". The bell's sheet shows it once on; ≡ Settings
 * shows the same section, and while they're off a way into the sheet.
 *
 * Where push can't work it says why instead: an iPhone in Safari needs the home screen
 * (the guide is one tap away); a browser that said no gets the steps to undo it, in its
 * own words; a subscription that vanished gets "Turn back on". The last switch off deletes
 * this device's push address on the api.
 */
import { type ReactNode, useId, useState } from 'react';

import { busyButton } from '../components/busy.ts';
import { useNestsVisible } from '../nests/store.ts';
import type { PlatformInfo } from '../pwa/platform.ts';
import { Switch } from '../ui/Switch.tsx';
import { sendTest, setPref, setTime, type TestResult, testLine, turnOn, useNotifyStatus } from './push.ts';
import { type HalfHour, type PushPrefs, type SwitchKey, TIME_CHOICES, type TimeKey } from './state.ts';
import '../styles/push.css';

/** How to allow notifications again after "Don't Allow", in this browser's own words. */
export function undoSteps(p: PlatformInfo): string {
	if (p.ios) return 'To turn them back on, open your iPhone’s Settings, then Notifications, then Sparrow.';
	if (p.android && p.standalone) return 'To turn them back on, press and hold Sparrow’s icon, tap App info, then Notifications.';
	if (p.android) return 'To turn them back on, tap the icon to the left of the address, then Permissions, then Notifications.';
	if (p.macSafari) return 'To turn them back on, open Safari’s Settings, then Websites, then Notifications, and allow sparrowword.com.';
	return 'To turn them back on, click the icon to the left of the address, then Site settings, and allow notifications.';
}

/**
 * Why a switch didn't take (QA2-12): offline, or the api didn't answer, is the connection;
 * online with no push service (no service worker, Brave with Google's push off) is the browser,
 * and trying again won't help.
 */
export function failureLine(result: 'failed' | 'unavailable', lead = 'Couldn’t change that notification.'): string {
	const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
	if (result === 'failed' || offline) return `${lead} Check your connection, then try again.`;
	const brave = typeof navigator !== 'undefined' && 'brave' in navigator;
	if (brave)
		return 'This browser can’t take notifications from Sparrow. In Brave, turn on “Use Google services for push messaging” in Settings, then Privacy and security.';
	return 'This browser can’t take notifications from Sparrow.';
}

// ------------------------------------------------------------------------------ the icons

const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/** The kinds' small printed icons (the mock's tiles). */
export const KindIcon: Record<'morning' | 'streakSaver' | 'nests' | 'roundUps' | 'nudges' | 'sundayTable' | 'beatTime' | 'quiet', () => ReactNode> = {
	morning: () => (
		<svg viewBox="0 0 24 24" {...line} aria-hidden="true">
			<circle cx="12" cy="12" r="4" />
			<path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4" />
		</svg>
	),
	streakSaver: () => (
		<svg viewBox="0 0 24 24" {...line} aria-hidden="true">
			<path d="M19 14.5A7.5 7.5 0 0 1 9.5 5a7.5 7.5 0 1 0 9.5 9.5z" fill="currentColor" />
		</svg>
	),
	nests: () => (
		<svg viewBox="0 0 24 24" {...line} aria-hidden="true">
			<circle cx="12" cy="12" r="7.5" />
			<circle cx="12" cy="12" r="3.5" />
		</svg>
	),
	roundUps: () => (
		<svg viewBox="0 0 24 24" {...line} aria-hidden="true">
			<circle cx="12" cy="12" r="7.5" />
			<circle cx="12" cy="12" r="3.5" />
		</svg>
	),
	nudges: () => (
		<svg viewBox="0 0 24 24" {...line} aria-hidden="true">
			<path d="M8 12V6.5a1.5 1.5 0 0 1 3 0V11M11 10V5a1.5 1.5 0 0 1 3 0v5M14 10V6.5a1.5 1.5 0 0 1 3 0V14a6 6 0 0 1-6 6h-.5a5.5 5.5 0 0 1-4.6-2.5L4.5 15a1.5 1.5 0 0 1 2.4-1.8L8 14.5" />
		</svg>
	),
	sundayTable: () => (
		<svg viewBox="0 0 24 24" {...line} aria-hidden="true">
			<rect x="5" y="4.5" width="14" height="15" rx="1.5" />
			<path d="M8 9h8M8 12h8M8 15h5" />
		</svg>
	),
	beatTime: () => (
		<svg viewBox="0 0 24 24" {...line} aria-hidden="true">
			<circle cx="12" cy="13.5" r="6.5" />
			<path d="M12 10.5v3l2 1.5M10 3.5h4M12 3.5V7" />
		</svg>
	),
	quiet: () => (
		<svg viewBox="0 0 24 24" {...line} aria-hidden="true">
			<path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" />
			<path d="M4 4l16 16" />
		</svg>
	),
};

// ------------------------------------------------------------------------------- the rows

/**
 * One kind: its icon, label and line, an optional time, and its switch (ui/Switch). The
 * switch is its own control (a time sits between), named by the label and described by the line.
 */
export function KindRow({
	icon,
	label,
	sub,
	time,
	checked,
	onToggle,
	busy = false,
	working = false,
	testId,
}: {
	icon: ReactNode;
	label: string;
	sub: ReactNode;
	time?: ReactNode;
	checked: boolean;
	onToggle: () => void;
	busy?: boolean;
	working?: boolean;
	testId?: string;
}) {
	const id = useId();
	return (
		<div className="nk-row">
			<span className="nk-ic">{icon}</span>
			<span className="nk-lb">
				<span id={`${id}-l`}>{label}</span>
				<small id={`${id}-s`}>{sub}</small>
			</span>
			{time}
			<span className="nk-sw">
				<Switch
					checked={checked}
					onCheckedChange={onToggle}
					busy={busy}
					working={working}
					labelledBy={`${id}-l`}
					describedBy={`${id}-s`}
					testId={testId}
				/>
			</span>
		</div>
	);
}

/** A time as a printed chip over the native picker (the phone's own wheel or list). */
export function TimeChip({ value, choices, label, onChange, testId }: { value: HalfHour; choices: readonly HalfHour[]; label: string; onChange: (v: HalfHour) => void; testId?: string }) {
	return (
		<span className="nk-time">
			<select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
				{choices.map((t) => (
					<option key={t} value={t}>
						{t}
					</option>
				))}
			</select>
			<span className="nk-time-face" aria-hidden="true">
				{value}
				<svg viewBox="0 0 10 6">
					<path d="M1 1l4 4 4-4" />
				</svg>
			</span>
		</span>
	);
}

// ------------------------------------------------------------------------- the full list

const NEST_KINDS: { key: SwitchKey; label: string; sub: string }[] = [
	{ key: 'roundUps', label: 'Nest round-ups', sub: '19:30 on days someone plays' },
	{ key: 'nudges', label: 'Nudges', sub: 'When it’s your go' },
	{ key: 'sundayTable', label: 'Sunday’s table', sub: 'The week’s feathers, Sundays 20:00' },
	{ key: 'beatTime', label: 'Beat your time', sub: 'When someone in your nest is quicker' },
];

/** The quiet hours' line: what they hold back, or that there are none. */
export function quietLine(prefs: Pick<PushPrefs, 'quietFrom' | 'quietTo'>): string {
	if (prefs.quietFrom === prefs.quietTo) return 'Off: nest notifications can come any time.';
	return 'Nest notifications wait until they end. Your reminders come at your times.';
}

/**
 * Once on: every kind, the times, quiet hours and the test. Each change goes to the api
 * as it's made (push.ts), and the last switch off turns notifications off here.
 */
export function NotifyKinds({ onStopped }: { onStopped?: () => void }) {
	const { push } = useNotifyStatus();
	const nests = useNestsVisible();
	const [busy, setBusy] = useState<SwitchKey | null>(null);
	const [problem, setProblem] = useState<string | null>(null);
	const [test, setTest] = useState<{ busy: boolean; result: TestResult | null }>({ busy: false, result: null });
	const prefs = push.prefs;

	const flip = async (key: SwitchKey) => {
		if (busy) return;
		setBusy(key);
		setProblem(null);
		const result = await setPref(key, !prefs[key]).catch(() => 'failed' as const);
		setBusy(null);
		if (result === 'failed' || result === 'unavailable') setProblem(failureLine(result));
	};
	const time = async (key: TimeKey, value: HalfHour) => {
		setProblem(null);
		const result = await setTime(key, value);
		if (result === 'failed') setProblem(failureLine('failed', 'Couldn’t change that time.'));
	};
	const sendOne = async () => {
		setTest({ busy: true, result: null });
		const result = await sendTest();
		setTest({ busy: false, result });
		if (result === 'gone') onStopped?.();
	};

	const row = (key: SwitchKey, label: string, sub: ReactNode, icon: ReactNode, timeEl?: ReactNode) => (
		<li key={key}>
			<KindRow
				icon={icon}
				label={label}
				sub={sub}
				time={timeEl}
				checked={prefs[key]}
				busy={busy !== null}
				working={busy === key}
				onToggle={() => void flip(key)}
				testId={`push-${key}`}
			/>
		</li>
	);

	return (
		<div className="nk" data-testid="notify-kinds">
			<ul className="nk-list">
				{row(
					'morning',
					'Morning reminder',
					'If you haven’t played yet',
					<KindIcon.morning />,
					<TimeChip value={prefs.morningAt} choices={TIME_CHOICES.morningAt} label="Morning reminder time" onChange={(v) => void time('morningAt', v)} testId="push-morningAt" />,
				)}
				{row(
					'streakSaver',
					'Streak saver',
					'Before your streak ends',
					<KindIcon.streakSaver />,
					<TimeChip value={prefs.streakSaverAt} choices={TIME_CHOICES.streakSaverAt} label="Streak saver time" onChange={(v) => void time('streakSaverAt', v)} testId="push-streakSaverAt" />,
				)}
				{nests && NEST_KINDS.map((k) => row(k.key, k.label, k.sub, KindIcon[k.key as 'roundUps']!()))}
			</ul>
			{nests && (
				<div className="nk-quiet" role="group" aria-labelledby="nk-quiet-l" aria-describedby="nk-quiet-s">
					<span className="nk-ic">
						<KindIcon.quiet />
					</span>
					<span className="nk-lb">
						<span id="nk-quiet-l">Quiet hours</span>
						<small id="nk-quiet-s">{quietLine(prefs)}</small>
					</span>
					<span className="nk-span">
						<TimeChip value={prefs.quietFrom} choices={TIME_CHOICES.quietFrom} label="Quiet hours from" onChange={(v) => void time('quietFrom', v)} testId="push-quietFrom" />
						<span className="nk-to" aria-hidden="true">
							to
						</span>
						<TimeChip value={prefs.quietTo} choices={TIME_CHOICES.quietTo} label="Quiet hours to" onChange={(v) => void time('quietTo', v)} testId="push-quietTo" />
					</span>
				</div>
			)}
			{problem && (
				<p className="nk-note nk-err" role="alert">
					{problem}
				</p>
			)}
			<button type="button" className="btn sm secondary nk-test" {...busyButton(test.busy, () => void sendOne())} data-testid="push-test">
				Send a test notification
			</button>
			<p className="nk-note" role="status">
				{test.result ? testLine(test.result) : ''}
			</p>
		</div>
	);
}

// --------------------------------------------------------------------- ≡ Settings section

/**
 * Settings → Notifications: the same list once on; while off, a way into the bell's sheet
 * (the only place the browser is asked). Hidden while the api has notifications off, and
 * in an app's own browser.
 */
export function NotificationSettings({ onInstall, onOpenSheet }: { onInstall: () => void; onOpenSheet: () => void }) {
	const { state, platform, push } = useNotifyStatus();
	const [busy, setBusy] = useState(false);
	const [problem, setProblem] = useState<string | null>(null);

	if (!state || platform.inApp) return null;

	const again = async () => {
		setBusy(true);
		setProblem(null);
		const result = await turnOn(push.prefs, 'settings').catch(() => 'failed' as const);
		setBusy(false);
		if (result === 'failed' || result === 'unavailable') setProblem(failureLine(result, 'Couldn’t turn them back on.'));
	};

	let body: ReactNode;
	if (state === 'unsupported') {
		body = <p className="mn-note">This browser can’t show notifications.</p>;
	} else if (state === 'needs_home') {
		body = (
			<>
				<p className="mn-note">On an iPhone, notifications need Sparrow on your home screen. Add it, then turn them on there.</p>
				<button type="button" className="btn sm secondary push-act" onClick={onInstall}>
					Add to home screen
				</button>
			</>
		);
	} else if (state === 'blocked') {
		body = (
			<p className="mn-note" data-testid="push-denied">
				Notifications are blocked for Sparrow. {undoSteps(platform)}
			</p>
		);
	} else if (state === 'stopped') {
		body = (
			<>
				<p className="mn-note" role="status">
					Notifications stopped on this device.
				</p>
				<button type="button" className="btn sm secondary push-act" {...busyButton(busy, () => void again())}>
					Turn back on
				</button>
			</>
		);
	} else if (state === 'on') {
		body = <NotifyKinds />;
	} else {
		body = (
			<button type="button" className="btn sm secondary push-act" onClick={onOpenSheet} data-testid="settings-push-open">
				Turn on notifications
			</button>
		);
	}

	return (
		<section className="mn-sec push-set" role="group" aria-labelledby="set-push-k" data-testid="settings-push">
			<p className="mn-k" id="set-push-k">
				Notifications
			</p>
			{body}
			{problem && (
				<p className="mn-note mn-err" role="alert">
					{problem}
				</p>
			)}
		</section>
	);
}
